import { Link } from "react-router-dom";
import { Bookmark, List, Moon, Sun } from "lucide-react";
import { useI18n } from "@/i18n";
import styles from "@/components/library/ReaderVisual.module.css";

const SIZES = [14, 16, 18, 20];
const iconBtn =
  "inline-flex h-11 w-11 items-center justify-center rounded-[8px] text-ax-text transition-colors duration-150 hover:bg-ax-surface";

export const ReaderBar = ({ size, onSize, theme, onTheme, bookmarked, onBookmark, onToc, savingBookmark = false }) => {
  const { t } = useI18n();
  return (
    <div
      data-testid="reader-bar"
      className={`sticky top-14 z-10 flex h-14 items-center justify-between gap-1 md:top-0 ${styles.bar}`}
    >
      <button type="button" onClick={onToc} aria-label={t("reader.toc")} title={t("reader.toc")} data-testid="reader-toc-button" className={`${iconBtn} md:hidden`}>
        <List size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>
      <div role="group" aria-label={t("reader.size")} className="flex items-center">
        {SIZES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onSize(s)}
            aria-pressed={size === s}
            aria-label={t("reader.sizeValue", { n: s })}
            data-testid={`reader-size-${s}`}
            className={`inline-flex h-11 min-w-[44px] items-center justify-center rounded-[8px] px-1 font-mono text-[13px] ${
              size === s ? "tint-chip-active border text-ax-link" : "text-ax-text-2 hover:bg-ax-surface"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="flex items-center">
        <button type="button" onClick={onTheme} aria-label={t(theme === "dark" ? "reader.toLight" : "reader.toDark")} title={t(theme === "dark" ? "reader.toLight" : "reader.toDark")} data-testid="reader-theme-button" className={iconBtn}>
          {theme === "dark" ? <Sun size={20} strokeWidth={1.5} aria-hidden="true" /> : <Moon size={20} strokeWidth={1.5} aria-hidden="true" />}
        </button>
        <button
          type="button"
          onClick={onBookmark}
          disabled={savingBookmark}
          aria-busy={savingBookmark}
          aria-pressed={bookmarked}
          aria-label={t(bookmarked ? "reader.removeBookmark" : "reader.addBookmark")}
          title={t(bookmarked ? "reader.removeBookmark" : "reader.addBookmark")}
          data-testid="reader-bookmark-button"
          className={iconBtn}
        >
          <Bookmark size={20} strokeWidth={1.5} aria-hidden="true" fill={bookmarked ? "currentColor" : "none"} />
        </button>
      </div>
    </div>
  );
};

export const Toc = ({ book, current, onPick }) => {
  const { lang } = useI18n();
  return (
    <ol data-testid="reader-toc" className={`m-0 flex list-none flex-col gap-1 p-0 ${styles.toc}`}>
      {book.chapters.map((c, i) => (
        <li key={c.id}>
          <Link
            to={`/library/${book.id}/bab/${c.id}`}
            onClick={onPick}
            aria-current={c.id === current ? "page" : undefined}
            data-testid={`toc-item-${c.id}`}
            className={`flex min-h-[44px] items-center gap-3 rounded-[8px] px-2 text-sm leading-5 ${
              c.id === current ? "font-medium text-ax-link" : "text-ax-text-2 hover:bg-ax-surface"
            }`}
          >
            <span className="font-mono text-[13px] text-ax-muted">{String(i + 1).padStart(2, "0")}</span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{c.title[lang]}</span>
          </Link>
        </li>
      ))}
    </ol>
  );
};
